import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaSave } from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import './Promotions.css';

const EditPromotion = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    discountType: 'percentage',
    discountValue: '',
    minOrderAmount: '',
    maxUsesTotal: '',
    validFrom: '',
    validUntil: '',
    maxUsesPerUser: '',
    status: 'ACTIVE'
  });

  useEffect(() => {
    const fetchPromotion = async () => {
      try {
        const response = await api.get(`${ENDPOINTS.PROMOTIONS.base}/${id}`);
        const promo = response.data;
        setFormData({
          code: promo.code,
          name: promo.name,
          description: promo.description || '',
          discountType: promo.discountType,
          discountValue: promo.discountValue,
          minOrderAmount: promo.minOrderAmount || '',
          maxUsesTotal: promo.maxUsesTotal || '',
          validFrom: promo.validFrom.split('T')[0],
          validUntil: promo.validUntil.split('T')[0],
          maxUsesPerUser: promo.maxUsesPerUser || '',
          status: promo.status
        });
      } catch (error) {
        console.error('Failed to fetch promotion:', error);
        toast.error('Failed to load promotion details');
        navigate('/promotions');
      } finally {
        setLoading(false);
      }
    };
    fetchPromotion();
  }, [id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`${ENDPOINTS.PROMOTIONS.base}/${id}`, {
        ...formData,
        discountValue: parseFloat(formData.discountValue),
        minOrderAmount: formData.minOrderAmount ? parseFloat(formData.minOrderAmount) : null,
        maxUsesTotal: formData.maxUsesTotal ? parseInt(formData.maxUsesTotal) : null,
        maxUsesPerUser: formData.maxUsesPerUser ? parseInt(formData.maxUsesPerUser) : null,
      });
      toast.success('Promotion updated successfully!');
      navigate('/promotions');
    } catch (error) {
      console.error('Failed to update promotion:', error);
      toast.error('Failed to update promotion: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Loading promotion details...</p>
      </div>
    );
  }

  return (
    <div className="create-promotion-page">
      <div className="page-header">
        <button onClick={() => navigate('/promotions')} className="btn btn-secondary">
          <FaArrowLeft /> Back to Promotions
        </button>
      </div>

      <div className="content-card">
        <h3 className="card-title">Edit Promotion</h3>
        
        <form onSubmit={handleSubmit} className="promotion-form">
          <div className="form-row">
            <div className="form-group">
              <label>Promotion Code</label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                className="form-control"
                required
                disabled // Code should typically not be editable
              />
            </div>
            <div className="form-group">
              <label>Promotion Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="form-control"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Discount Type *</label>
              <select
                name="discountType"
                value={formData.discountType}
                onChange={handleChange}
                className="form-control"
                required
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
            <div className="form-group">
              <label>Discount Value *</label>
              <input
                type="number"
                name="discountValue"
                value={formData.discountValue}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Minimum Order Amount</label>
              <input
                type="number"
                name="minOrderAmount"
                value={formData.minOrderAmount}
                onChange={handleChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Maximum Uses (Total)</label>
              <input
                type="number"
                name="maxUsesTotal"
                value={formData.maxUsesTotal}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Valid From *</label>
              <input
                type="date"
                name="validFrom"
                value={formData.validFrom}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
            <div className="form-group">
              <label>Valid Until *</label>
              <input
                type="date"
                name="validUntil"
                value={formData.validUntil}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Max Uses Per User</label>
              <input
                type="number"
                name="maxUsesPerUser"
                value={formData.maxUsesPerUser}
                onChange={handleChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-control"
              >
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
                <option value="expired">Expired</option>
                <option value="scheduled">Scheduled</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/promotions')}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              <FaSave /> Update Promotion
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPromotion;
